import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import { collection, query, where, orderBy, limit, onSnapshot, doc, getDoc } from "firebase/firestore";
import { useCall } from "../../context/CallContext";
import { MdCallMade, MdCallReceived, MdCallMissed, MdVideocam, MdPhone, MdCallEnd } from "react-icons/md";
import { useTheme } from "../../context/ThemeContext";

const CallHistoryList = () => {
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const { initiateVoiceCall, initiateVideoCall } = useCall();
    const currentUid = auth.currentUser?.uid;
    const { isDarkMode } = useTheme();

    useEffect(() => {
        if (!currentUid) return;

        // Fetch Call Logs (No orderBy to avoid index requirement)
        const q = query(
            collection(db, "call_logs"),
            where("participants", "array-contains", currentUid),
            limit(100)
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const callData = [];
            const userCache = {}; // Cache to avoid duplicate fetches

            for (const docSnap of snapshot.docs) {
                const data = docSnap.data();
                const otherUserId = data.callerId === currentUid ? data.receiverId : data.callerId;

                let otherUserName = "Unknown";
                let otherUserPhoto = null;

                if (userCache[otherUserId]) {
                    otherUserName = userCache[otherUserId].name;
                    otherUserPhoto = userCache[otherUserId].photo;
                } else {
                    try {
                        const userDoc = await getDoc(doc(db, "users", otherUserId));
                        if (userDoc.exists()) {
                            const uData = userDoc.data();
                            otherUserName = uData.username || uData.email;
                            otherUserPhoto = uData.photoURL;
                            userCache[otherUserId] = { name: otherUserName, photo: otherUserPhoto };
                        }
                    } catch (e) {
                        console.error("Error fetching user", e);
                    }
                }

                callData.push({
                    id: docSnap.id,
                    ...data,
                    otherUserId,
                    otherUserName,
                    otherUserPhoto
                });
            }

            // Sort in-memory to avoid index requirement
            const sortedData = callData.sort((a, b) => {
                const getMs = (ts) => {
                    if (!ts) return 0;
                    if (ts.toMillis) return ts.toMillis();
                    if (ts.seconds) return ts.seconds * 1000;
                    return ts;
                };
                return getMs(b.timestamp) - getMs(a.timestamp);
            });

            setCalls(sortedData);
            setLoading(false);
        }, (error) => {
            console.error("Call history load error:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUid]);

    const handleRedial = (otherId, isVideo) => {
        // Logic to recreate conversation ID consistent with App
        // chatID logic: if (current < other) current_other else other_current
        const chatId = currentUid < otherId ? `${currentUid}_${otherId}` : `${otherId}_${currentUid}`;

        if (isVideo) {
            initiateVideoCall(chatId, otherId);
        } else {
            initiateVoiceCall(chatId, otherId);
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return { time: "", label: "" };
        let date;
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (typeof timestamp === 'number') {
            date = new Date(timestamp);
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            return { time: "", label: "" };
        }

        const now = new Date();
        const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        if (date.toDateString() === now.toDateString()) {
            return { time: timeStr, label: "Today" };
        }

        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) {
            return { time: timeStr, label: "Yesterday" };
        }

        return { time: timeStr, label: date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) };
    };

    if (loading) {
        return <div className="p-4 text-center opacity-50">Loading calls...</div>;
    }

    if (calls.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full opacity-50 mt-10">
                <MdPhone className="text-5xl mb-4" />
                <p className="text-lg font-medium">No recent calls</p>
                <p className="text-sm">Your call history will appear here</p>
            </div>
        );
    }

    // Group calls by label
    const groupedCalls = calls.reduce((groups, call) => {
        const { label } = formatTime(call.timestamp);
        if (!groups[label]) {
            groups[label] = [];
        }
        groups[label].push(call);
        return groups;
    }, {});

    return (
        <div className="flex flex-col space-y-4 p-2 pb-24">
            {Object.keys(groupedCalls).map((groupLabel) => (
                <div key={groupLabel} className="space-y-2">
                    <div className={`px-2 py-1 text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {groupLabel}
                    </div>
                    {groupedCalls[groupLabel].map((call) => {
                        const isMissed = call.status === "missed" || (call.status === "rejected" && call.callerId !== currentUid);
                        const isOutgoing = call.callerId === currentUid;
                        const { time } = formatTime(call.timestamp);

                        let StatusIcon = MdCallReceived;
                        let iconColor = isDarkMode ? "text-emerald-400" : "text-emerald-500";

                        if (isOutgoing) {
                            StatusIcon = MdCallMade;
                            iconColor = isDarkMode ? "text-gray-400" : "text-gray-500";
                        } else if (isMissed) {
                            StatusIcon = MdCallMissed;
                            iconColor = "text-red-500";
                        }

                        return (
                            <div key={call.id} className={`flex items-center justify-between p-3.5 rounded-2xl transition-all ${isDarkMode ? 'hover:bg-white/10 bg-white/5 active:bg-white/15' : 'hover:bg-black/5 bg-white shadow-sm border border-black/5 active:bg-black/10'}`}>
                                <div className="flex items-center gap-4 flex-1">
                                    <div className="relative flex-shrink-0">
                                        {call.otherUserPhoto ? (
                                            <img src={call.otherUserPhoto} alt={call.otherUserName} className="w-12 h-12 rounded-full object-cover shadow-sm ring-1 ring-black/5" />
                                        ) : (
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md`}>
                                                {call.otherUserName[0]?.toUpperCase()}
                                            </div>
                                        )}
                                        <div className={`absolute -bottom-1 -right-1 p-1 rounded-full border-2 ${isDarkMode ? 'bg-[#0f172a] border-[#0f172a]' : 'bg-white border-white'}`}>
                                            {call.isVideo ? <MdVideocam className="text-[10px] text-blue-500" /> : <MdPhone className="text-[10px] text-green-500" />}
                                        </div>
                                    </div>

                                    <div className="flex flex-col min-w-0">
                                        <span className={`font-bold text-base truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} ${isMissed ? 'text-red-500' : ''}`}>
                                            {call.otherUserName}
                                        </span>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <StatusIcon className={`text-sm ${iconColor}`} />
                                            <span className={`text-[13px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {isMissed ? "Missed" : (isOutgoing ? "Outgoing" : "Incoming")} • {time}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => handleRedial(call.otherUserId, call.isVideo)}
                                        title={`Redial ${call.isVideo ? 'Video' : 'Voice'}`}
                                        className={`p-3 rounded-full transition-all group ${isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-blue-50 text-blue-600'}`}
                                    >
                                        {call.isVideo ? <MdVideocam size={22} className="group-hover:scale-110 transition-transform" /> : <MdPhone size={20} className="group-hover:scale-110 transition-transform" />}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default CallHistoryList;
